/**
 * Pure helpers for collapsing resolved ``ask_user`` clarifications into
 * the "Answers" summary card. Each resolved clarification carries the
 * question the agent asked plus the answer the user supplied (the option
 * label they picked, the free text they typed, or a skipped marker).
 *
 * Kept separate from the React surface so the parsing — mapping the
 * wire-level ``selected_option_ids`` back to their human labels — can be
 * unit-tested without spinning up the renderer.
 */
import type { ChatItem, ToolUseItem } from '../../../contracts/harness.types';
import {
  OTHER_WIRE_VALUE,
  parseInput,
  parseQuestions,
} from '../../harness/ask-user-details';

import { isClarificationToolName } from '../../../contracts/ask-user-details.types';
import { readToolResultRecord } from '../../../contracts/projects/harness/toolResult';
import { readToolResultText } from '../../../contracts/toolResult';

export interface ResolvedClarification {
  id: string;
  /** The question the agent asked. ``undefined`` only for malformed input. */
  question: string | undefined;
  /**
   * The answer to show beneath the question — the picked option label(s),
   * the typed free text, or the "Other" detail. ``null`` when the user
   * skipped it, or when the resolved answer isn't available yet (the brief
   * SSE-only window before the run snapshot carries ``modified_input``).
   */
  answer: string | null;
  /** The user dismissed the question instead of answering. */
  isDismissed: boolean;
  /**
   * Self-fetching ``pick_session`` answers submit a bare session id with no
   * agent-supplied option label. When set, the Answers card fetches session
   * meta for this id and falls back to ``answer`` (the raw id) on miss.
   */
  unresolvedSessionId?: string;
  /**
   * Self-fetching ``pick_template`` answers submit a bare template id with no
   * agent-supplied option label. When set, the Answers card resolves the
   * display name from the document-templates list.
   */
  unresolvedTemplateId?: string;
}

/**
 * A ``tool_use`` item is a resolved clarification once it leaves the
 * pending/running states — i.e. the user has answered (``complete``) or
 * dismissed it (``error`` / ``cancelled``). The active card lives in the
 * composer dock, so those in-flight states are never summarised here.
 */
export const isResolvedClarification = (item: ChatItem): item is ToolUseItem =>
  item.kind === 'tool_use' &&
  isClarificationToolName(item.name) &&
  (item.status === 'complete' || item.status === 'error' || item.status === 'cancelled');

const readSelectedOptionIds = (record: Record<string, unknown> | undefined): string[] =>
  Array.isArray(record?.selected_option_ids)
    ? record.selected_option_ids.filter((v): v is string => typeof v === 'string')
    : [];

/**
 * Pull the human answer out of an answer-shaped record
 * (``{ selected_option_ids, text }``) — the wire shape the inline card
 * submits, echoed back both on the resolved ``approval.modified_input``
 * and on the tool result the agent receives.
 */
const readAnswerFromRecord = (
  record: Record<string, unknown> | undefined,
  optionLabelById: Map<string, string>
): string | null => {
  if (!record) return null;

  const selectedIds = readSelectedOptionIds(record);
  const text = typeof record.text === 'string' ? record.text.trim() : '';

  const parts: string[] = [];
  for (const id of selectedIds) {
    // The "Other" choice carries its detail in ``text`` — surface that
    // rather than the literal "other" sentinel.
    if (id === OTHER_WIRE_VALUE) {
      if (text) parts.push(text);
      continue;
    }
    const label = optionLabelById.get(id);
    if (label !== undefined) {
      parts.push(label);
    } else if (text) {
      // Self-fetching pickers (e.g. pick_ehr "Other") submit a provider id
      // with no static label map — the typed name lives in ``text``.
      parts.push(text);
    } else {
      parts.push(id);
    }
  }

  if (parts.length > 0) return parts.join(', ');
  if (text) return text;
  return null;
};

/**
 * When a self-fetching picker (``pick_session`` / ``pick_template``) has no
 * agent-supplied option labels, the answer string is the raw id. Tag that
 * single id so the Answers card can fetch display meta. Multi-select without
 * labels stays as joined ids.
 */
const unresolvedSelfFetchPickId = (
  kind: string | undefined,
  targetKind: 'pick_session' | 'pick_template',
  record: Record<string, unknown> | undefined,
  optionLabelById: Map<string, string>,
  answer: string | null
): string | undefined => {
  if (kind !== targetKind || !answer || !record) return undefined;
  const unlabeled = readSelectedOptionIds(record).filter(
    (id) => id !== OTHER_WIRE_VALUE && !optionLabelById.has(id)
  );
  if (unlabeled.length !== 1) return undefined;
  return unlabeled[0] === answer ? unlabeled[0] : undefined;
};

export const unresolvedPickSessionId = (
  kind: string | undefined,
  record: Record<string, unknown> | undefined,
  optionLabelById: Map<string, string>,
  answer: string | null
): string | undefined =>
  unresolvedSelfFetchPickId(kind, 'pick_session', record, optionLabelById, answer);

export const unresolvedPickTemplateId = (
  kind: string | undefined,
  record: Record<string, unknown> | undefined,
  optionLabelById: Map<string, string>,
  answer: string | null
): string | undefined =>
  unresolvedSelfFetchPickId(kind, 'pick_template', record, optionLabelById, answer);

const readAnswerText = (item: ToolUseItem): string | null => {
  const parsed = parseInput(item.input);
  const optionLabelById = new Map<string, string>();
  if (parsed) {
    for (const option of parsed.options) optionLabelById.set(option.id, option.label);
  }

  // The resolved snapshot carries the answer on ``modified_input`` — but
  // only after the run snapshot refetches. In the live window right after
  // the user answers, that's still absent, so fall back to the tool result
  // the agent received (same answer shape, lands via the result-blocks SSE).
  const fromApproval = readAnswerFromRecord(
    item.approval?.modified_input ?? undefined,
    optionLabelById
  );
  if (fromApproval) return fromApproval;

  const fromResult = readAnswerFromRecord(readToolResultRecord(item), optionLabelById);
  if (fromResult) return fromResult;

  // Last resort: a short, single-line plain-text result (skip JSON dumps —
  // those were already handled structurally above).
  const text = readToolResultText(item)?.trim();
  if (text && text.length <= 280 && !text.includes('\n') && !/^[[{]/.test(text)) {
    return text;
  }
  return null;
};

const readRecordFromSource = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : undefined;

const readAnswersMap = (item: ToolUseItem): Record<string, unknown> | undefined => {
  const fromApproval = readRecordFromSource(item.approval?.modified_input)?.answers;
  if (typeof fromApproval === 'object' && fromApproval !== null) {
    return fromApproval as Record<string, unknown>;
  }
  const fromResult = readToolResultRecord(item)?.answers;
  if (typeof fromResult === 'object' && fromResult !== null) {
    return fromResult as Record<string, unknown>;
  }
  return undefined;
};

const readAnswerRecordForLegacy = (
  item: ToolUseItem
): Record<string, unknown> | undefined => {
  const fromApproval = item.approval?.modified_input ?? undefined;
  if (fromApproval) return fromApproval;
  return readToolResultRecord(item);
};

const isClarificationDismissed = (item: ToolUseItem): boolean =>
  item.status === 'cancelled' ||
  item.approval?.status === 'denied' ||
  item.approval?.status === 'timed_out' ||
  item.status === 'error';

export const extractResolvedClarification = (
  item: ToolUseItem
): ResolvedClarification => {
  const parsed = parseInput(item.input);
  const rawQuestion = (item.input as { question?: unknown }).question;
  const question =
    parsed?.question ||
    (typeof rawQuestion === 'string' ? rawQuestion.trim() : '') ||
    undefined;
  const isDismissed = isClarificationDismissed(item);
  const optionLabelById = new Map<string, string>();
  if (parsed) {
    for (const option of parsed.options) optionLabelById.set(option.id, option.label);
  }
  const answer = isDismissed ? null : readAnswerText(item);
  const record = readAnswerRecordForLegacy(item);
  return {
    id: item.id,
    question,
    answer,
    isDismissed,
    unresolvedSessionId: isDismissed
      ? undefined
      : unresolvedPickSessionId(parsed?.kind, record, optionLabelById, answer),
    unresolvedTemplateId: isDismissed
      ? undefined
      : unresolvedPickTemplateId(parsed?.kind, record, optionLabelById, answer),
  };
};

export const extractResolvedClarifications = (
  item: ToolUseItem
): ResolvedClarification[] => {
  const questions = parseQuestions(item.input);
  if (!questions || questions.length === 0) return [extractResolvedClarification(item)];

  const answers = readAnswersMap(item);
  const isDismissed = isClarificationDismissed(item);
  return questions.map((question) => {
    const record = readRecordFromSource(answers?.[question.id]);
    const optionLabelById = new Map(
      question.options.map((option) => [option.id, option.label])
    );
    const answer = isDismissed ? null : readAnswerFromRecord(record, optionLabelById);
    const skipped = isDismissed || answers?.[question.id] === null;
    return {
      id: `${item.id}:${question.id}`,
      question: question.question,
      answer,
      isDismissed: skipped,
      unresolvedSessionId: skipped
        ? undefined
        : unresolvedPickSessionId(question.kind, record, optionLabelById, answer),
      unresolvedTemplateId: skipped
        ? undefined
        : unresolvedPickTemplateId(question.kind, record, optionLabelById, answer),
    };
  });
};

/** Session display fields for Answers / picker rows — mirrors SessionPickerList. */
export const readSessionAnswerMeta = (session: {
  session_name?: string | null;
  patient?: { name?: string | null } | null;
}): { sessionName: string; patientName?: string } => {
  const sessionName = session.session_name?.trim() ?? '';
  const patientName = session.patient?.name?.trim() || undefined;
  return { sessionName, patientName };
};
